import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { JobCandidateFormDialogComponent } from 'src/app/components/main/employees/rrhh/job-vacancy-candidate-form-dialog/job-vacancy-candidate-form-dialog.component';
import { JobVacancyFormDialogComponent } from 'src/app/components/main/employees/rrhh/job-vacancy-form-dialog/job-vacancy-form-dialog.component';
import { JobVacancyPhaseTracklineComponent } from 'src/app/components/main/employees/rrhh/job-vacancy-phase-trackline/job-vacancy-phase-trackline.component';
import { JobVacancyPhasesConfigDialogComponent } from 'src/app/components/main/employees/rrhh/job-vacancy-phases-config-dialog/job-vacancy-phases-config-dialog.component';
import { JobVacancyPhaseTypeEnumLabels } from 'src/app/enums/job-vacancy-phase-type.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { JobCandidate, JobCandidateSerialized } from 'src/app/models/job_candidate.model';
import { JobVacancy, JobVacancyPhase } from 'src/app/models/job_vacancy.model';
import { TrustedHtmlPipe } from 'src/app/pipes/trusted-html.pipe';
import { ApiService } from 'src/app/services';
import dayjs from 'dayjs';

type CandidateFilterValue = 'active' | 'discarded' | 'all';

@Component({
  selector: 'app-job-vacancy-detail',
  templateUrl: './job-vacancy-detail.component.html',
  styleUrl: './job-vacancy-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CurrencyPipe,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatOptionModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    JobVacancyPhaseTracklineComponent,
    TrustedHtmlPipe,
  ],
})
export class JobVacancyDetailComponent implements OnInit {
  private readonly api_service = inject(ApiService);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly snack_bar = inject(MatSnackBar);

  readonly job_vacancy = signal<JobVacancy | null>(null);
  readonly sorted_vacancy_phases = signal<JobVacancyPhase[]>([]);
  readonly loading = signal(true);

  candidates_data_source = new MatTableDataSource<JobCandidateSerialized>([]);
  candidates_displayed_columns: string[] = ['full_name', 'user', 'email', 'current_phase', 'actions'];

  readonly department_categories_label = signal('-');
  readonly period_label = signal('-');
  readonly status_label = signal('-');

  readonly candidate_filter = signal<CandidateFilterValue>('active');
  readonly pipeline_candidates = signal<JobCandidate[]>([]);

  readonly candidate_filter_options: { value: CandidateFilterValue; label: string }[] = [
    { value: 'active', label: 'Activos' },
    { value: 'discarded', label: 'Descartados' },
    { value: 'all', label: 'Todos' },
  ];

  protected job_vacancy_id = 0;

  ngOnInit(): void {
    this.job_vacancy_id = Number(this.activated_route.snapshot.paramMap.get('id'));

    if (!this.job_vacancy_id) {
      return;
    }

    this.loadVacancy();
    this.loadCandidates();
    this.loadPipelineCandidates();
  }

  openEditVacancyDialog(): void {
    const job_vacancy = this.job_vacancy();
    if (!job_vacancy) {
      return;
    }

    const dialog_ref = this.mat_dialog.open(JobVacancyFormDialogComponent, {
      width: '640px',
      data: { mode: 'edit', job_vacancy },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.loadVacancy();
          this.loadPipelineCandidates();
        }
      });
  }

  openPhasesConfigDialog(): void {
    const job_vacancy = this.job_vacancy();

    if (!job_vacancy || (job_vacancy.job_vacancy_phases?.length ?? 0) === 0) {
      return;
    }

    const dialog_ref = this.mat_dialog.open(JobVacancyPhasesConfigDialogComponent, {
      width: '720px',
      data: { job_vacancy },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.loadVacancy();
        }
      });
  }

  onCandidatePhaseChange(job_candidate: JobCandidateSerialized, change: MatSelectChange): void {
    if (job_candidate.is_discarded) {
      return;
    }

    const job_vacancy_phase_id = change.value as number;

    this.api_service
      .updateJobCandidate(job_candidate.id, { job_vacancy_phase_id })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.loadCandidates();
          this.loadPipelineCandidates();
          this.snack_bar.open('Fase actualizada', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          this.loadCandidates();
          const message = err?.error?.error ?? 'Error al cambiar la fase.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  openCreateCandidateDialog(): void {
    const job_vacancy = this.job_vacancy();
    if (!job_vacancy) {
      return;
    }

    const dialog_ref = this.mat_dialog.open(JobCandidateFormDialogComponent, {
      width: '520px',
      data: {
        job_vacancy_id: job_vacancy.id,
      },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.loadCandidates();
          this.loadPipelineCandidates();
        }
      });
  }

  onCandidateFilterChange(change: MatSelectChange): void {
    this.candidate_filter.set(change.value as CandidateFilterValue);
    this.loadCandidates();
  }

  discardCandidate(job_candidate: JobCandidateSerialized): void {
    if (!confirm(`¿Descartar a "${job_candidate.full_name}"?`)) {
      return;
    }

    this.api_service
      .discardJobCandidate(job_candidate.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.loadCandidates();
          this.loadPipelineCandidates();
          this.snack_bar.open('Candidato descartado', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al descartar.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  private loadVacancy(): void {
    this.loading.set(true);

    this.api_service
      .getJobVacancy(this.job_vacancy_id, {
        with: 'department_categories.active_department_category_variable_condition,job_vacancy_phases',
        append: 'salary_range',
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (job_vacancy: JobVacancy): void => {
          const phases_with_labels =
            job_vacancy.job_vacancy_phases?.map(phase => ({
              ...phase,
              type_label: JobVacancyPhaseTypeEnumLabels[phase.type] ?? phase.type,
            })) ?? [];

          const vacancy_with_phases = {
            ...job_vacancy,
            job_vacancy_phases: phases_with_labels,
          };

          this.job_vacancy.set(vacancy_with_phases);
          this.sorted_vacancy_phases.set(
            [...phases_with_labels].sort((first_phase, second_phase) => first_phase.sort_order - second_phase.sort_order)
          );
          this.department_categories_label.set(
            job_vacancy.department_categories?.map(department_category => department_category.name).join(', ') || '-'
          );
          this.period_label.set(this.buildPeriodLabel(job_vacancy));
          this.status_label.set(this.isJobVacancyCurrentlyActive(job_vacancy) ? 'Activa' : 'Inactiva');
          this.loading.set(false);
          this.change_detector_ref.markForCheck();
        },
        error: (): void => {
          this.loading.set(false);
          this.snack_bar.open('No se pudo cargar la vacante.', undefined, { duration: 5000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private loadCandidates(): void {
    const params: Record<string, unknown> = {
      job_vacancy_id: this.job_vacancy_id,
      per_page: 100,
      order_by: 'full_name:asc',
      with: 'active_job_vacancy_candidate_phase_progress.job_vacancy_phase,job_vacancy_candidate_phase_progress.job_vacancy_phase,user',
    };

    const candidate_filter = this.candidate_filter();
    if (candidate_filter === 'active') {
      params.scopes = 'withoutDiscarded';
    } else if (candidate_filter === 'discarded') {
      params.scopes = 'discarded';
    }

    this.api_service
      .getJobCandidatesPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<JobCandidate>): void => {
          this.candidates_data_source.data = response.data.map(job_candidate => this.serializeCandidate(job_candidate));
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private loadPipelineCandidates(): void {
    this.api_service
      .getJobCandidatesPaged({
        job_vacancy_id: this.job_vacancy_id,
        per_page: 100,
        scopes: 'withoutDiscarded',
        with: 'active_job_vacancy_candidate_phase_progress.job_vacancy_phase,job_vacancy_candidate_phase_progress.job_vacancy_phase,user',
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<JobCandidate>): void => {
          this.pipeline_candidates.set(response.data);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private buildPeriodLabel(job_vacancy: JobVacancy): string {
    const start_label = dayjs(job_vacancy.start_date).format('DD/MM/YYYY');
    const end_label = job_vacancy.end_date ? dayjs(job_vacancy.end_date).format('DD/MM/YYYY') : '—';

    return `${start_label} → ${end_label}`;
  }

  private isJobVacancyCurrentlyActive(job_vacancy: JobVacancy): boolean {
    const now = dayjs();
    const has_started = dayjs(job_vacancy.start_date).isSameOrBefore(now);
    const has_not_ended = job_vacancy.end_date === null || dayjs(job_vacancy.end_date).isSameOrAfter(now);

    return has_started && has_not_ended;
  }

  private serializeCandidate(job_candidate: JobCandidate): JobCandidateSerialized {
    const user = job_candidate.user;
    const current_phase_id = this.resolveCurrentPhaseId(job_candidate);
    const current_phase_label = this.resolveCurrentPhaseLabel(job_candidate, current_phase_id);
    const is_discarded = this.isJobCandidateDiscarded(job_candidate);

    return {
      ...job_candidate,
      user_label: user ? `${user.name} ${user.surnames}`.trim() : '-',
      current_phase_label,
      current_phase_id,
      is_discarded,
    };
  }

  private resolveCurrentPhaseId(job_candidate: JobCandidate): number | null {
    const active_progress = job_candidate.active_job_vacancy_candidate_phase_progress;

    if (active_progress?.job_vacancy_phase_id != null) {
      return active_progress.job_vacancy_phase_id;
    }

    const open_progress = job_candidate.job_vacancy_candidate_phase_progress?.find(
      progress => progress.completed_at === null && progress.discarded_at === null
    );

    if (open_progress?.job_vacancy_phase_id != null) {
      return open_progress.job_vacancy_phase_id;
    }

    const sorted_phases = this.sorted_vacancy_phases();

    return sorted_phases[0]?.id ?? null;
  }

  private resolveCurrentPhaseLabel(job_candidate: JobCandidate, current_phase_id: number | null): string {
    const active_progress = job_candidate.active_job_vacancy_candidate_phase_progress;

    if (active_progress?.job_vacancy_phase?.name) {
      return active_progress.job_vacancy_phase.name;
    }

    const open_progress = job_candidate.job_vacancy_candidate_phase_progress?.find(
      progress => progress.completed_at === null && progress.discarded_at === null
    );

    if (open_progress?.job_vacancy_phase?.name) {
      return open_progress.job_vacancy_phase.name;
    }

    if (current_phase_id !== null) {
      const job_vacancy_phase = this.sorted_vacancy_phases().find(phase => phase.id === current_phase_id);

      return job_vacancy_phase?.name ?? '-';
    }

    return '-';
  }

  private isJobCandidateDiscarded(job_candidate: JobCandidate): boolean {
    const has_open_progress = job_candidate.job_vacancy_candidate_phase_progress?.some(
      progress => progress.completed_at === null && progress.discarded_at === null
    );

    if (has_open_progress) {
      return false;
    }

    return job_candidate.job_vacancy_candidate_phase_progress?.some(progress => progress.discarded_at !== null) ?? false;
  }
}
